import { useEffect, useRef, useState } from 'react';
import { useConversation } from '@elevenlabs/react';
import { Lead } from '@/types/lead';

interface UseColdCallProps {
  lead: Lead;
  product: string;
  onEnd: () => void;
}

export enum CallStatus {
  DIALING = 'dialing',
  CONNECTED = 'connected',
  TALKING = 'talking',
  ENDED = 'ended',
  NO_ANSWER = 'no_answer',
  BUSY = 'busy'
}

export const useColdCall = ({ lead, product, onEnd }: UseColdCallProps) => {
  const [callStatus, setCallStatus] = useState<CallStatus>(CallStatus.DIALING);
  const [messages, setMessages] = useState<Array<{ role: string; content: string; timestamp: Date }>>([]);
  const [duration, setDuration] = useState(0);
  const conversation = useConversation();
  const startTimeRef = useRef<Date | null>(null);

  // Simulate dialing process
  useEffect(() => {
    if (callStatus === CallStatus.DIALING) {
      const timer = setTimeout(() => {
        // Simulate successful connection 90% of the time
        if (Math.random() > 0.1) {
          setCallStatus(CallStatus.CONNECTED);
          startTimeRef.current = new Date();
        } else {
          setCallStatus(CallStatus.NO_ANSWER);
          setTimeout(onEnd, 2000);
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [callStatus, onEnd]);

  // Track call duration
  useEffect(() => {
    if (callStatus === CallStatus.CONNECTED || callStatus === CallStatus.TALKING) {
      const interval = setInterval(() => {
        if (startTimeRef.current) {
          setDuration(Math.floor((Date.now() - startTimeRef.current.getTime()) / 1000));
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [callStatus]);

  const startConversation = async () => {
    setCallStatus(CallStatus.TALKING);

    // Mock API call to get session config
    const session = {
      aiConfig: {
        agentId: import.meta.env.VITE_11LABS_AGENT_ID || 'default-agent',
        initialPrompt: generateSystemPrompt(),
        voiceId: 'professional-sales-voice',
        language: 'en-US'
      }
    };

    await conversation.startSession({
      agentId: session.aiConfig.agentId,
      connectionType: 'webrtc',
      overrides: {
        agent: {
          prompt: {
            prompt: generateSystemPrompt()
          },
          firstMessage: `Good morning! May I speak with ${lead.name}? This is Sarah from Prudential Singapore.`,
          language: session.aiConfig.language as any,
        },
        tts: {
          voiceId: session.aiConfig.voiceId,
        },
      },
      onMessage: ({ source, message }: { source: string; message: string }) => {
        setMessages(prev => [...prev, {
          role: source,
          content: message,
          timestamp: new Date()
        }]);
      }
    });
  };

  const endCall = async () => {
    await conversation.endSession();
    setCallStatus(CallStatus.ENDED);
    onEnd();
  };

  const generateSystemPrompt = () => {
    const productPrompts = {
      prushield: `You are calling about PruShield, Prudential's comprehensive health insurance plan that covers hospitalization and surgical expenses.`,
      pruactive: `You are calling about PruActive, Prudential's wellness-focused insurance plan that rewards healthy living with premium discounts.`
    };

    return `You are a professional insurance sales representative from Prudential Singapore making a cold call to ${lead.name} at ${lead.company}.

Your objective is to:
1. Introduce yourself professionally
2. Build rapport by mentioning their role as ${lead.title} in the ${lead.industry} industry
3. Identify their company's health insurance needs
4. ${productPrompts[product as keyof typeof productPrompts] || productPrompts.prushield}
5. Try to schedule a follow-up meeting or send more information

Key points to remember:
- Be respectful of their time
- Listen actively to their concerns
- Address objections professionally
- Focus on value, not just features
- Company size: ${lead.companySize || 'Unknown'}
${lead.painPoints ? `- Known pain points: ${lead.painPoints.join(', ')}` : ''}

Start with: "Good morning/afternoon, may I speak with ${lead.name}? This is [Your Name] from Prudential Singapore..."`;
  };

  return {
    callStatus,
    messages,
    duration,
    conversation,
    startConversation,
    endCall,
    systemPrompt: generateSystemPrompt()
  };
};